/**
 * The occasion registry — metadata for every date the app can schedule against.
 *
 * ⚠ ADMIN-IMPORT-ONLY, like everything in this directory. The storefront reads
 * baked windows and never learns that occasions exist.
 *
 * ⚠ NO `t()` CALLS. Labels here are English DEVELOPER strings and stable ids.
 * The admin re-keys at the call site — `t(\`occasions.names.\${o.id}\`)` — because
 * this registry is also imported by the Worker's recompute cron, which has no
 * i18n and no merchant locale. xbar shipped a generated catalogue in English to
 * twenty languages by getting exactly this wrong.
 */

export type OccasionClass =
  /** Fixed Gregorian date. Correct forever. */
  | 'F'
  /** Closed-form algorithm. Correct forever. */
  | 'A'
  /** Generated from ICU at build time and frozen. Correct within the tabular calendar. */
  | 'G'
  /** Human-verified table. Correct only to HORIZON_YEAR. */
  | 'T';

/**
 * How exactly the date is known.
 *
 * `approx_1d` is rendered honestly in the UI rather than hidden: Islamic dates
 * are sighting-dependent and genuinely vary by ±1 day BY COUNTRY. A merchant who
 * is told that can add a day of padding; one who is not will find out from a
 * customer.
 */
export type Precision = 'exact' | 'approx_1d';

export interface Occasion {
  id: string;
  /** English developer label. NOT user copy — see the header. */
  label: string;
  cls: OccasionClass;
  precision: Precision;
  /**
   * Suggested lead-in / run-out, in days, offered as the default when a merchant
   * picks this occasion. A merchant selling Christmas wants weeks; one marking
   * April Fools' wants the day.
   */
  pad: { before: number; after: number };
  /** ISO 3166-1 alpha-2 codes where this is primarily observed. Empty = global. */
  regions?: string[];
  /** Selectable regional readings, e.g. Diwali as observed in South India. */
  variants?: Array<{ id: string; label: string }>;
  /**
   * True when the occasion's THEME depends on hemisphere — a "Winter Sale" is
   * June in Sydney and December in Oslo. Only seasonal occasions carry it;
   * Christmas is December everywhere regardless of the weather.
   */
  seasonal?: boolean;
  /** Grouping for the picker. */
  group: 'major' | 'retail' | 'seasonal' | 'cultural' | 'national';
}

export const OCCASIONS: Occasion[] = [
  // ── the ones that carry the year ───────────────────────────────────────────
  {
    id: 'christmas',
    label: 'Christmas',
    cls: 'F',
    precision: 'exact',
    pad: { before: 30, after: 1 },
    group: 'major',
  },
  {
    id: 'boxingday',
    label: 'Boxing Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'retail',
  },
  {
    id: 'advent',
    label: 'Advent',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 27 },
    group: 'major',
  },
  {
    id: 'newyearseve',
    label: "New Year's Eve",
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    group: 'major',
  },
  {
    id: 'newyear',
    label: "New Year's Day",
    cls: 'F',
    precision: 'exact',
    pad: { before: 0, after: 2 },
    group: 'major',
  },
  {
    id: 'halloween',
    label: 'Halloween',
    cls: 'F',
    precision: 'exact',
    pad: { before: 14, after: 0 },
    group: 'major',
  },
  {
    id: 'valentines',
    label: "Valentine's Day",
    cls: 'F',
    precision: 'exact',
    pad: { before: 10, after: 0 },
    group: 'major',
  },
  {
    id: 'easter',
    label: 'Easter (Western)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 7, after: 1 },
    group: 'major',
  },
  {
    id: 'easter-orthodox',
    label: 'Easter (Orthodox)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 7, after: 1 },
    group: 'major',
  },

  // ── retail moments ─────────────────────────────────────────────────────────
  // BFCM is the single highest-value template in the product: Thanksgiving − 1
  // through Cyber Monday, which is the whole five-day weekend.
  {
    id: 'bfcm',
    label: 'Black Friday / Cyber Monday',
    cls: 'A',
    precision: 'exact',
    pad: { before: 1, after: 4 },
    group: 'retail',
  },
  {
    id: 'blackfriday',
    label: 'Black Friday',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'retail',
  },
  {
    id: 'cybermonday',
    label: 'Cyber Monday',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'retail',
  },
  {
    id: 'singles-day',
    label: "Singles' Day (11.11)",
    cls: 'F',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['CN', 'SG', 'MY'],
    group: 'retail',
  },
  {
    id: 'free-shipping-day',
    label: 'Free Shipping Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'retail',
  },
  {
    id: 'mothersday',
    label: "Mother's Day (US/CA/AU/JP)",
    cls: 'A',
    precision: 'exact',
    pad: { before: 10, after: 0 },
    group: 'retail',
  },
  {
    id: 'mothersday-uk',
    label: 'Mothering Sunday (UK/IE)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 10, after: 0 },
    regions: ['GB', 'IE'],
    group: 'retail',
  },
  {
    id: 'fathersday',
    label: "Father's Day",
    cls: 'A',
    precision: 'exact',
    pad: { before: 10, after: 0 },
    // AU/NZ keep it in September and Brazil in August. One date for all three
    // would be wrong for two of them.
    variants: [
      { id: 'au', label: 'Australia / New Zealand (1st Sun Sep)' },
      { id: 'br', label: 'Brazil (2nd Sun Aug)' },
    ],
    group: 'retail',
  },

  // ── seasonal, hemisphere-dependent ─────────────────────────────────────────
  {
    id: 'spring-start',
    label: 'Spring',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 89 },
    seasonal: true,
    group: 'seasonal',
  },
  {
    id: 'summer-start',
    label: 'Summer',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 92 },
    seasonal: true,
    group: 'seasonal',
  },
  {
    id: 'autumn-start',
    label: 'Autumn',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 90 },
    seasonal: true,
    group: 'seasonal',
  },
  {
    id: 'winter-start',
    label: 'Winter',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 89 },
    seasonal: true,
    group: 'seasonal',
  },

  // ── Class G — lunisolar, generated from ICU ────────────────────────────────
  {
    id: 'ramadan',
    label: 'Ramadan',
    cls: 'G',
    precision: 'approx_1d',
    pad: { before: 0, after: 29 },
    group: 'cultural',
  },
  {
    id: 'eid-al-fitr',
    label: 'Eid al-Fitr',
    cls: 'G',
    precision: 'approx_1d',
    pad: { before: 2, after: 2 },
    group: 'cultural',
  },
  {
    id: 'eid-al-adha',
    label: 'Eid al-Adha',
    cls: 'G',
    precision: 'approx_1d',
    pad: { before: 2, after: 3 },
    group: 'cultural',
  },
  {
    id: 'lunar-new-year',
    label: 'Lunar New Year',
    cls: 'G',
    precision: 'exact',
    pad: { before: 7, after: 7 },
    regions: ['CN', 'SG', 'MY', 'VN', 'TW', 'HK'],
    group: 'cultural',
  },
  {
    id: 'lantern-festival',
    label: 'Lantern Festival',
    cls: 'G',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'cultural',
  },
  {
    id: 'dragon-boat',
    label: 'Dragon Boat Festival',
    cls: 'G',
    precision: 'exact',
    pad: { before: 1, after: 1 },
    group: 'cultural',
  },
  {
    id: 'qixi',
    label: 'Qixi Festival',
    cls: 'G',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    group: 'cultural',
  },
  {
    id: 'mid-autumn',
    label: 'Mid-Autumn Festival',
    cls: 'G',
    precision: 'exact',
    pad: { before: 5, after: 1 },
    group: 'cultural',
  },
  {
    id: 'seollal',
    label: 'Seollal',
    cls: 'G',
    precision: 'exact',
    pad: { before: 2, after: 2 },
    regions: ['KR'],
    group: 'cultural',
  },
  {
    id: 'chuseok',
    label: 'Chuseok',
    cls: 'G',
    precision: 'exact',
    pad: { before: 2, after: 2 },
    regions: ['KR'],
    group: 'cultural',
  },
  {
    id: 'rosh-hashanah',
    label: 'Rosh Hashanah',
    cls: 'G',
    precision: 'exact',
    pad: { before: 3, after: 1 },
    group: 'cultural',
  },
  {
    id: 'yom-kippur',
    label: 'Yom Kippur',
    cls: 'G',
    precision: 'exact',
    pad: { before: 1, after: 0 },
    group: 'cultural',
  },
  {
    id: 'hanukkah',
    label: 'Hanukkah',
    cls: 'G',
    precision: 'exact',
    pad: { before: 2, after: 7 },
    group: 'cultural',
  },
  {
    id: 'passover',
    label: 'Passover',
    cls: 'G',
    precision: 'exact',
    pad: { before: 3, after: 7 },
    group: 'cultural',
  },
  {
    id: 'purim',
    label: 'Purim',
    cls: 'G',
    precision: 'exact',
    pad: { before: 1, after: 0 },
    group: 'cultural',
  },

  // ── Class T — human-verified tables ────────────────────────────────────────
  {
    id: 'diwali',
    label: 'Diwali',
    cls: 'T',
    precision: 'exact',
    pad: { before: 5, after: 2 },
    regions: ['IN', 'SG', 'MY', 'LK', 'NP'],
    variants: [{ id: 'south', label: 'South India (Naraka Chaturdashi)' }],
    group: 'cultural',
  },
  {
    id: 'holi',
    label: 'Holi',
    cls: 'T',
    precision: 'exact',
    pad: { before: 2, after: 1 },
    regions: ['IN', 'NP'],
    group: 'cultural',
  },
  {
    id: 'raksha-bandhan',
    label: 'Raksha Bandhan',
    cls: 'T',
    precision: 'exact',
    pad: { before: 5, after: 0 },
    regions: ['IN'],
    group: 'cultural',
  },
  {
    id: 'navratri',
    label: 'Navratri',
    cls: 'T',
    precision: 'exact',
    pad: { before: 2, after: 9 },
    regions: ['IN'],
    group: 'cultural',
  },
  // ⚠ `onam` is deliberately ABSENT.
  //
  // Thiruvonam depends on the Malayalam solar month and the Thiruvonam
  // nakshatra, so there is no lunar-phase check that can corroborate it — and
  // the two reachable published sources disagree on 2026, the nearest and most
  // important year. Offering a Kerala festival on the wrong day to Kerala
  // merchants is worse than not offering it. See tables.ts.
  // Class A, not T: the March equinox measured at Tehran is exactly computable,
  // so this needs no table, no sources and no horizon. The old hand-typed table
  // had 2026 and 2030 wrong by a day. See astro.ts.
  {
    id: 'nowruz',
    label: 'Nowruz',
    cls: 'A',
    precision: 'exact',
    pad: { before: 3, after: 12 },
    regions: ['IR', 'AF', 'AZ', 'TJ', 'UZ'],
    group: 'cultural',
  },
  {
    id: 'loy-krathong',
    label: 'Loy Krathong',
    cls: 'T',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['TH'],
    group: 'cultural',
  },
  {
    id: 'pongal',
    label: 'Pongal / Makar Sankranti',
    cls: 'F',
    precision: 'exact',
    pad: { before: 1, after: 3 },
    regions: ['IN'],
    group: 'cultural',
  },
  {
    id: 'songkran',
    label: 'Songkran',
    cls: 'F',
    precision: 'exact',
    pad: { before: 1, after: 2 },
    regions: ['TH'],
    group: 'cultural',
  },
  {
    id: 'day-of-the-dead',
    label: 'Día de los Muertos',
    cls: 'F',
    precision: 'exact',
    pad: { before: 1, after: 1 },
    regions: ['MX'],
    group: 'cultural',
  },

  // ── national and civic ─────────────────────────────────────────────────────
  {
    id: 'independence-day-us',
    label: 'Independence Day (US)',
    cls: 'F',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'canada-day',
    label: 'Canada Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['CA'],
    group: 'national',
  },
  {
    id: 'bastille-day',
    label: 'Bastille Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['FR'],
    group: 'national',
  },
  {
    id: 'independence-day-in',
    label: 'Independence Day (India)',
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['IN'],
    group: 'national',
  },
  {
    id: 'republic-day-in',
    label: 'Republic Day (India)',
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['IN'],
    group: 'national',
  },
  {
    id: 'thanksgiving-us',
    label: 'Thanksgiving (US)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'thanksgiving-ca',
    label: 'Thanksgiving (Canada)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['CA'],
    group: 'national',
  },
  {
    id: 'labor-day-us',
    label: 'Labor Day (US)',
    cls: 'A',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'memorial-day',
    label: 'Memorial Day',
    cls: 'A',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'mlk-day',
    label: 'Martin Luther King Jr. Day',
    cls: 'A',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'presidents-day',
    label: "Presidents' Day",
    cls: 'A',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    regions: ['US'],
    group: 'national',
  },
  {
    id: 'may-day',
    label: 'May Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 1, after: 0 },
    group: 'national',
  },
  {
    id: 'womens-day',
    label: "International Women's Day",
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    group: 'national',
  },
  {
    id: 'earth-day',
    label: 'Earth Day',
    cls: 'F',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    group: 'national',
  },
  {
    id: 'stpatricks',
    label: "St Patrick's Day",
    cls: 'F',
    precision: 'exact',
    pad: { before: 3, after: 0 },
    group: 'national',
  },
  {
    id: 'april-fools',
    label: "April Fools' Day",
    cls: 'F',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'national',
  },
  {
    id: 'oktoberfest',
    label: 'Oktoberfest',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 16 },
    regions: ['DE', 'AT'],
    group: 'national',
  },
  {
    id: 'mardi-gras',
    label: 'Mardi Gras',
    cls: 'A',
    precision: 'exact',
    pad: { before: 2, after: 0 },
    group: 'national',
  },
  {
    id: 'good-friday',
    label: 'Good Friday',
    cls: 'A',
    precision: 'exact',
    pad: { before: 0, after: 0 },
    group: 'national',
  },
];

export const OCCASIONS_BY_ID: Record<string, Occasion> = Object.fromEntries(
  OCCASIONS.map((o) => [o.id, o])
);

// ─────────────────────────────────────────────────────────────────────────────
// Hemisphere
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Countries whose seasons are inverted relative to the northern default.
 *
 * A merchant in Sydney who picks "Winter Sale" means June, and an app that
 * schedules December is not slightly wrong — it is unusable. Kept as an explicit
 * list rather than a latitude lookup because the question is "which season does
 * this merchant's market keep", which is political, not geographic: Brazil and
 * Indonesia both straddle the equator and answer it differently.
 */
export const SOUTHERN_COUNTRIES = new Set([
  'AU',
  'NZ',
  'AR',
  'CL',
  'UY',
  'PY',
  'ZA',
  'BW',
  'NA',
  'ZW',
  'MZ',
  'MG',
  'PE',
  'BO',
  'BR',
  'FJ',
  'PG',
  'NC',
  'LS',
  'SZ',
  'ZM',
  'MW',
  'AO',
]);

export type Hemisphere = 'north' | 'south';

export function hemisphereOf(countryCode: string | null | undefined): Hemisphere {
  return countryCode && SOUTHERN_COUNTRIES.has(countryCode.toUpperCase()) ? 'south' : 'north';
}

/** First day of each season, `[month, day]`, by hemisphere. */
export const SEASON_MONTHS: Record<Hemisphere, Record<string, [number, number]>> = {
  north: {
    'spring-start': [3, 1],
    'summer-start': [6, 1],
    'autumn-start': [9, 1],
    'winter-start': [12, 1],
  },
  south: {
    'spring-start': [9, 1],
    'summer-start': [12, 1],
    'autumn-start': [3, 1],
    'winter-start': [6, 1],
  },
};
